import { SaleItemState, SaleOrderItemState } from "@/types/sales";
import { App, Button, DatePicker, Empty, Flex, Input, message, notification, TableProps, Tooltip } from "antd";
import { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDataList from "@/hooks/useDataList";
import api, { ApprovalListProps } from "@/apis/order";
import { CommonStateEnum, SourceTypeEnum } from "@/types/common";
import useDataDel from "@/hooks/useDataDel";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import useOrderColumn from "../hooks/useOrderColumn";
import { CustomTabPaneContext } from "@/components/TabPane";
import { Dayjs } from "dayjs";
import CreateRequirement from "../../../../Requirement/pages/Create";
import ChangeForm from "@/pages/Warehouse/components/ChangeForm";
import { ChangeEnum } from "@/types/inventory";

import RequirementDetail from "@/pages/Requirement/pages/Detail";
import usePage from "@/layout/hooks/usePage";
import { PageId } from "@/types/page";
import RequirementHandleAdd from "@/pages/Requirement/pages/HandleAdd";
import { CloseOutlined } from "@ant-design/icons";
import SelectCustomer from "../../../../../components/Selector/SelectCustomer";
import { RequirementStatusEnum } from "@/types/requirement";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import ExportExcelToPI from "./ExportExcelToPI";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { countries } from "@/utils/country";
import { DictContext } from "@/layout/context/DictContext";
import { dateFormat, priceFormat } from "@/utils/format";
import dayjs from "dayjs";
import ItemsOrderForm from "@/pages/Sales/components/ItemsOrderForm";
import SText from "@/styles/SText";
import { hasPermission } from "@/utils/user";
import { UserPermissionEnum } from "@/types/user";
import SaleSwapModal from "@/pages/Sales/components/SaleSwapModal";
import { swapEnter } from "@/utils/swapEnter";
import SaleOut from "./SaleOut";
import PurSwapModal from "@/pages/Sales/components/PurSwapModal";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";
import { parseBatchSearch } from "@/utils/batchSearch";
import PrintSaleOrder from "./PrintSaleOrder";
const { RangePicker } = DatePicker;

interface SalesOrderFilters {
  keyword: string;
  customerId: string;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
}

const INITIAL_SALES_ORDER_FILTERS: SalesOrderFilters = {
  keyword: "",
  customerId: "",
  startTime: null,
  endTime: null,
};

const OrderList: FC = () => {
  const { t } = useTranslation();
  const { pageId } = usePage();

  const { onOpen } = useContext(CustomTabPaneContext);
  const { currencys, saleTradeTerm, salePayMethod, saleShippingMethod, saleTypes, creditLevels, orgs } = useContext(DictContext);
  const { curUser } = useSelector((state: RootState) => state.userReducer)

  // 导出列配置只依赖语言，避免勾选时重新创建列配置传给导出组件。
  const orderColumns = useMemo(() => [
    { key: 'sale.orderNumber', label: t('sale.orderNumber') },
    { key: 'sale.orderState', label: t('sale.orderState') },
    { key: 'sale.innerNumber', label: t('sale.innerNumber') },
    { key: 'sale.shortName', label: t('sale.shortName') },
    { key: 'product.code', label: t('product.code') },
    { key: 'sale.customerModel', label: t('sale.customerModel') },
    { key: 'sale.mfgr', label: t('sale.mfgr') },
    { key: 'sale.quantity', label: t('sale.quantity') },
    { key: 'sale.reqDeliDate', label: t('sale.reqDeliDate') },
    { key: 'sale.smtStartDate', label: t('sale.smtStartDate') },
    { key: 'sale.preFinishDate', label: t('sale.preFinishDate') },
    { key: 'sale.taxPrice', label: t('sale.taxPrice') },
    { key: 'sale.aPrice', label: t('sale.aPrice') },
    { key: 'sale.discount', label: t('sale.discount') },
    { key: 'sale.sendQuantity', label: t('sale.sendQuantity') },
    { key: 'sale.sendAPrice', label: t('sale.sendAPrice') },
    { key: 'sale.sendDiscount', label: t('sale.sendDiscount') },
    { key: 'sale.currency', label: t('sale.currency') },
    { key: 'sale.totalPrice', label: t('sale.totalPrice') },
    { key: 'sale.tradeTerm', label: t('sale.tradeTerm') },
    { key: 'sale.payMethod', label: t('sale.payMethod') },
    { key: 'sale.depositRate', label: t('sale.depositRate') },
    { key: 'sale.specialReq', label: t('sale.specialReq') },
    { key: 'sale.reRemark', label: t('sale.reRemark') },
    { key: 'product.name', label: t('product.name') },
    { key: 'category.hsCode', label: t('category.hsCode') },
    { key: 'product.uom', label: t('product.uom') },
    { key: 'product.brand1', label: t('product.brand1') },
    { key: 'product.brand2', label: t('product.brand2') },
    { key: 'common.desc', label: t('common.desc') },
    { key: 'product.cbm', label: t('product.cbm') },
    { key: 'sale.receivingUnit', label: t('sale.receivingUnit') },
    { key: 'sale.receivingContact', label: t('sale.receivingContact') },
    { key: 'sale.receivingContactMobile', label: t('sale.receivingContactMobile') },
    { key: 'sale.receivingAddress', label: t('sale.receivingAddress') },
    { key: 'sale.revisePrice', label: t('sale.revisePrice') },
    { key: 'sale.actualPrice', label: t('sale.actualPrice') },
    { key: 'sale.shippingMethod', label: t('sale.shippingMethod') },
    { key: 'sale.code', label: t('sale.code') },
    { key: 'sale.name1', label: t('sale.name1') },
    { key: 'sale.name2', label: t('sale.name2') },
    { key: 'sale.country', label: t('sale.country') },
    { key: 'sale.address', label: t('sale.address') },
    { key: 'sale.zipCode', label: t('sale.zipCode') },
    { key: 'sale.email', label: t('sale.email') },
    { key: 'sale.contact', label: t('sale.contact') },
    { key: 'sale.phone', label: t('sale.phone') },
    { key: 'sale.fax', label: t('sale.fax') },
    { key: 'sale.mobile', label: t('sale.mobile') },
    { key: 'sale.account1', label: t('sale.account1') },
    { key: 'sale.bank1', label: t('sale.bank1') },
    { key: 'sale.swiftCode1', label: t('sale.swiftCode1') },
    { key: 'sale.account2', label: t('sale.account2') },
    { key: 'sale.bank2', label: t('sale.bank2') },
    { key: 'sale.swiftCode2', label: t('sale.swiftCode2') },
    { key: 'sale.saleType', label: t('sale.saleType') },
    { key: 'sale.creditLevel', label: t('sale.creditLevel') },
    { key: 'sale.creditLine', label: t('sale.creditLine') },
    { key: 'sale.org', label: t('sale.org') },
    { key: 'sale.taxRate', label: t('sale.taxRate') },
  ], [t]);

  // 导出数据映射函数
  const orderDataMapper = (item: SaleItemState, t: any, context: any) => ({
    [t('sale.orderNumber')]: item?.order?.number,
    [t('sale.orderState')]: item?.order?.preApproval ? t('sale.passed') : t('sale.notApproval'),
    [t('sale.innerNumber')]: item?.order?.innerNumber,
    [t('sale.shortName')]: item?.order?.shortName,
    [t('product.code')]: item?.product?.code,
    [t('sale.customerModel')]: item?.order?.customerModel,
    [t('sale.mfgr')]: item?.order?.mfgr,
    [t('sale.quantity')]: item?.quantity,
    [t('sale.reqDeliDate')]: item?.order?.reqDeliDate ? dateFormat(dayjs(item?.order?.reqDeliDate)) : '',
    [t('sale.smtStartDate')]: item?.order?.smtStartDate ? dateFormat(dayjs(item?.order?.smtStartDate)) : '',
    [t('sale.preFinishDate')]: item?.order?.preFinishDate ? dateFormat(dayjs(item?.order?.preFinishDate)) : '',
    [t('sale.taxPrice')]: priceFormat(item?.price ?? ''),
    [t('sale.aPrice')]: item?.discountedPrice ?? '',
    [t('sale.discount')]: item?.discount ?? '',
    [t('sale.sendQuantity')]: item?.bquantity ?? 0,
    [t('sale.sendAPrice')]: item?.bdiscountedPrice ?? '',
    [t('sale.sendDiscount')]: item?.bdiscount ?? '',
    [t('sale.currency')]: context.currencys.find((currency: any) => currency.key === item?.order.currency)?.desc,
    [t('sale.totalPrice')]: item?.order?.totalAmount,
    [t('sale.tradeTerm')]: context.saleTradeTerm.find((saleTrade: any) => saleTrade.id === item?.order.tradeTerm)?.key,
    [t('sale.payMethod')]: context.salePayMethod.find((salePay: any) => salePay.id === item?.order.payMethod)?.key,
    [t('sale.depositRate')]: item?.order?.depositRate ? item?.order?.depositRate + '%' : '',
    [t('sale.specialReq')]: item?.order?.specialReq,
    [t('sale.reRemark')]: item?.remark,
    [t('product.name')]: item?.product?.name,
    [t('category.hsCode')]: item?.product?.category?.hsCode ?? '',
    [t('product.uom')]: item?.product?.uom?.toLocaleUpperCase(),
    [t('product.brand1')]: item?.product?.model?.toLocaleUpperCase(),
    [t('product.brand2')]: item?.product?.t1?.toLocaleUpperCase(),
    [t('common.desc')]: item?.product?.desc,
    [t('product.cbm')]: item?.product?.cbm,
    [t('sale.receivingUnit')]: item?.order?.receiver,
    [t('sale.receivingContact')]: item?.order?.receiverContact,
    [t('sale.receivingContactMobile')]: item?.order?.receiverMobile,
    [t('sale.receivingAddress')]: item?.order?.receiverAddress,
    [t('sale.revisePrice')]: item?.order?.extPrice,
    [t('sale.actualPrice')]: (parseFloat(item?.order?.totalAmount || '0') + parseFloat(item?.order?.extPrice || '0')).toString(),
    [t('sale.shippingMethod')]: context.saleShippingMethod.find((sM: any) => sM.id === item?.order.shippingMethod)?.key,
    [t('sale.code')]: item?.order?.code,
    [t('sale.name1')]: item?.order?.name1,
    [t('sale.name2')]: item?.order?.name2,
    [t('sale.country')]: countries.find(country => country.code === item?.order.country)?.name,
    [t('sale.address')]: item?.order?.address,
    [t('sale.zipCode')]: item?.order?.zipCode,
    [t('sale.email')]: item?.order?.email,
    [t('sale.contact')]: item?.order?.contact,
    [t('sale.phone')]: item?.order?.phone,
    [t('sale.fax')]: item?.order?.fax,
    [t('sale.mobile')]: item?.order?.mobile,
    [t('sale.account1')]: item?.order?.account1,
    [t('sale.bank1')]: item?.order?.bank1,
    [t('sale.swiftCode1')]: item?.order?.swiftCode1,
    [t('sale.account2')]: item?.order?.account2,
    [t('sale.bank2')]: item?.order?.bank2,
    [t('sale.swiftCode2')]: item?.order?.swiftCode2,
    [t('sale.saleType')]: context.saleTypes.find((sM: any) => sM.id === item?.order.saleType)?.key,
    [t('sale.creditLevel')]: context.creditLevels.find((sM: any) => sM.key === item?.order.creditLevel)?.desc,
    [t('sale.creditLine')]: item?.order?.creditLine,
    [t('sale.org')]: context.orgs.find((org: any) => org.id === item?.order.orgId)?.name,
    [t('sale.taxRate')]: item?.order?.taxRate ? item?.order?.taxRate + '%' : '',
  });

  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { modal } = App.useApp();
  const [list, total, listLoading, { fetchList, addItem, rmItem, rmItems, updateItem }] = useDataList<SaleOrderItemState, [ApprovalListProps]>({ fetchApi: api.approvalOrderList });
  const { del } = useDataDel({ api: api.del });

  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<SalesOrderFilters>(INITIAL_SALES_ORDER_FILTERS);
  const { keyword, customerId, startTime, endTime } = draftFilters;
  const [addOpen, setAddOpen] = useState<boolean>(false);

  const [open, setOpen] = useState<boolean>(false);
  const [detail, setDetail] = useState<SaleOrderItemState | null>(null);
  const [financeItem, setFinanceItem] = useState<SaleOrderItemState | null>(null);

  const [requirement, setRequirement] = useState<SaleOrderItemState | null>(null);
  const [outOrder, setOutOrder] = useState<SaleItemState | null>(null);
  // 勾选时只维护 rowKey，避免把完整行对象写入 state 后触发表格和导出区域重渲染。
  const [selectPSaleIds, setSelectSaleIds] = useState<Array<React.Key>>([]);
  const listRef = useRef<Array<SaleOrderItemState>>([]);
  const selectedSaleIdsRef = useRef<Array<React.Key>>([]);
  const selectedSaleRowsRef = useRef<Map<string, SaleOrderItemState>>(new Map());
  const [swapSale, setSwapSale] = useState<SaleOrderItemState | null>(null);
  const [swapPurs, setSwapPurs] = useState<SaleOrderItemState[]>([]);

  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [saleOpen, setSaleOpen] = useState(false);

  const { onConfirm } = useFrontDelTip({ cb: (id: string) => onDel(id) });
  const columns = useOrderColumn({ onDetail: setDetail, onDel: onConfirm, onOpenTab: onOpen, onOpenFinanceRequire: setFinanceItem, onOut: setOutOrder, onOpenRequirement: setRequirement, openSwap: setSwapSale, openSwapPur: setSwapPurs });

  const curOrg = useSelector((state: RootState) => state.orgReducer.curOrg);
  // 导出 PI/销售出库在点击时再按 id 取完整行，勾选阶段不提前派生业务数据。
  listRef.current = list;
  selectedSaleIdsRef.current = selectPSaleIds;

  // 删除回调传给列操作区，保持引用稳定，避免 columns 因操作回调变化而重建。
  const onDel = useCallback((id: string) => {
    return del(id)
      .then(() => {
        rmItem(id);
        notification.success({ placement: 'top', message: t('common.rmSuccess') })
      })
      .catch(() => {
        notification.error({ placement: 'top', message: t('common.rmError') })

      });
  }, [del, notification, rmItem, t]);

  // 全量导出逻辑保持稳定引用，避免顶部工具栏在勾选时连带重建。
  const handleExportAll = useCallback(async (): Promise<SaleItemState[]> => {
    setExportLoading(true);
    setExportProgress(0);

    try {
      const batchSize = 100;
      let offset = 0;
      let allRecords: SaleOrderItemState[] = [];
      let hasMore = true;

      const pageTypeInfo = {
        [PageId.SALE_OFFICIAL]: {
          state: CommonStateEnum.ACTIVE
        },
        [PageId.F_SALE_OFFICIAL]: {
          state: CommonStateEnum.ACTIVE
        },
        [PageId.SALE_NOT_PASS]: {
          state: CommonStateEnum.UN_ACTIVE
        },
        [PageId.F_OFFICIAL_PENDING]: {
          state: CommonStateEnum.ACTIVE
        },
        [PageId.MC_OFFICIAL_PENDING]: {
          state: CommonStateEnum.ACTIVE
        },
        [PageId.PUR_OFFICIAL_PENDING]: {
          state: CommonStateEnum.ACTIVE
        },
        [PageId.PROD_OFFICIAL_PENDING]: {
          state: CommonStateEnum.ACTIVE
        },
      } as Partial<Record<PageId, { state: CommonStateEnum }>>;
      const defaultState = { state: CommonStateEnum.UN_ACTIVE };
      const searchParams = parseBatchSearch(appliedFilters.keyword);

      while (hasMore) {
        let params: ApprovalListProps = {
          params: {
            body: {
              customerId: appliedFilters.customerId,
              keyword: searchParams.keyword,
              requireStatus: undefined,
            },
            strs: searchParams.strs,
            pagination: { offset, take: batchSize },
            startTime: appliedFilters.startTime
              ? appliedFilters.startTime.startOf('day').valueOf()
              : undefined,
            endTime: appliedFilters.endTime
              ? appliedFilters.endTime.endOf('day').valueOf()
              : undefined,
          },
          orgId: curOrg.id,
          state: (pageTypeInfo[pageId!] || defaultState).state,
        };

        if (pageId === PageId.MC_NOT_PASS) {
          params.requirement = CommonStateEnum.UN_ACTIVE;
        }
        if (pageId === PageId.F_OFFICIAL_PENDING) {
          if (params.params?.body) {
            params.params.body.requireStatus = RequirementStatusEnum.OPEN_PENDING;
          }
        }
        if (pageId === PageId.F_OFFICIAL_CLOSED) {
          if (params.params?.body) {
            params.params.body.requireStatus = RequirementStatusEnum.CLOSE;
          }
        }
        if (pageId === PageId.F_OFFICIAL_DONE) {
          if (params.params?.body) {
            params.params.body.requireStatus = RequirementStatusEnum.OPEN_DONE;
          }
        }

        const response = await api.approvalOrderList(params);
        const records = response.body || [];
        allRecords = [...allRecords, ...records]

        if (records.length < batchSize) {
          hasMore = false;
        } else {
          offset += batchSize;
        }

        const progress = Math.min(
          Math.round((allRecords.length / (response.pagination?.size || allRecords.length)) * 100),
          99
        );
        setExportProgress(progress);
      }

      setExportProgress(100);
      let resList: SaleItemState[] = allRecords.map(row => ({
        ...row.items?.[0],
        remark: swapEnter(row.items?.[0]?.remark ?? ''),
        order: {
          ...row,
          items: []
        }
      }))
      return resList;
    } catch (error) {
      console.error('导出全部数据失败:', error);
      notification.error({ placement: 'top', message: t('common.error') });
      throw error;
    } finally {
      setExportLoading(false);
    }
  }, [curOrg.id, notification, pageId, appliedFilters, t]);

  const fetch = useCallback((
    offset: number,
    filters: SalesOrderFilters,
  ) => {
    const pageTypeInfo = {
      [PageId.SALE_OFFICIAL]: {
        state: CommonStateEnum.ACTIVE
      },
      [PageId.F_SALE_OFFICIAL]: {
        state: CommonStateEnum.ACTIVE
      },
      [PageId.SALE_NOT_PASS]: {
        state: CommonStateEnum.UN_ACTIVE
      },
      [PageId.F_OFFICIAL_PENDING]: {
        state: CommonStateEnum.ACTIVE
      },
      [PageId.MC_OFFICIAL_PENDING]: {
        state: CommonStateEnum.ACTIVE
      },
      [PageId.PUR_OFFICIAL_PENDING]: {
        state: CommonStateEnum.ACTIVE
      },
      [PageId.PROD_OFFICIAL_PENDING]: {
        state: CommonStateEnum.ACTIVE
      },
    } as Partial<Record<PageId, { state: CommonStateEnum }>>;
    const defaultState = { state: CommonStateEnum.UN_ACTIVE };
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE
    if (pageId) {
      // ✅ 取消上一次请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // ✅ 创建新的 AbortController
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const searchParams = parseBatchSearch(filters.keyword);

      let params: ApprovalListProps = {
        params: {
          body: {
            customerId: filters.customerId,
            keyword: searchParams.keyword,
            requireStatus: undefined,
          },
          strs: searchParams.strs,
          pagination: { offset, take },
          startTime: filters.startTime
            ? filters.startTime.startOf('day').valueOf()
            : undefined,
          endTime: filters.endTime
            ? filters.endTime.endOf('day').valueOf()
            : undefined,
          controller, // ✅ 传递 controller 用于取消请求
        },
        orgId: curOrg.id,
        state: (pageTypeInfo[pageId] || defaultState).state,
      }

      if (pageId === PageId.MC_NOT_PASS) {
        params.requirement = CommonStateEnum.UN_ACTIVE
        params.orgId = undefined
      }
      if (pageId === PageId.F_OFFICIAL_PENDING) {
        if (params.params?.body) {
          params.params.body.requireStatus = RequirementStatusEnum.OPEN_PENDING
        }
      }
      if (pageId === PageId.F_OFFICIAL_CLOSED) {
        if (params.params?.body) {
          params.params.body.requireStatus = RequirementStatusEnum.CLOSE
        }
      }
      if (pageId === PageId.F_OFFICIAL_DONE) {
        if (params.params?.body) {
          params.params.body.requireStatus = RequirementStatusEnum.OPEN_DONE
        }
      }
      if ([PageId.SALE_NOT_PASS, PageId.SALE_OFFICIAL].includes(pageId)) {
      }
      fetchList(params).catch((err) => {
        // ✅ 忽略取消的请求
        if (err?.name === 'AbortError') {
          return;
        }
        switch (err) {
          case 10010:
            break;
          default:
            break;
        }
      });
    }
  }, [curOrg.id, fetchList, pageId]);

  // pageId effect 只关心切页动作；用 ref 调最新 fetch，避免函数引用变化导致重复触发。
  const fetchRef = useRef(fetch);

  useEffect(() => {
    fetchRef.current = fetch;
  }, [fetch]);

  // ✅ pageId 变化时取消请求
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [pageId]);

  useEffect(() => {
    const selectedIdSet = new Set(selectedSaleIdsRef.current.map(String));
    // 跨页选中时，当前页重新请求后用最新行对象覆盖缓存，避免操作旧数据。
    list.forEach(item => {
      const id = String(item.id);
      if (selectedIdSet.has(id)) {
        selectedSaleRowsRef.current.set(id, item);
      }
    });
  }, [list]);

  useEffect(() => {
    fetchRef.current(0, appliedFilters);
    setSelectSaleIds([])
    selectedSaleRowsRef.current.clear();
    setDetail(null)
    // 页面切换时读取当前已生效条件，不让筛选提交重复触发此 effect。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  // 搜索按钮/回车使用稳定回调，减少 headerChildren 的引用变化。
  const handleSearch = useCallback(() => {
    const nextFilters = commitFilters();
    fetch(0, nextFilters);
    tableRef?.current?.resetTablePage();
  }, [commitFilters, fetch]);

  const handleClearFilters = useCallback(() => {
    const nextFilters = replaceFilters(INITIAL_SALES_ORDER_FILTERS);
    fetch(0, nextFilters);
    tableRef?.current?.resetTablePage();
  }, [fetch, replaceFilters]);

  // 分页回调传给 STableLayout，保持稳定避免表格属性无意义变化。
  const handleTableChange = useCallback((offset: number) => {
    fetch(offset, appliedFilters);
  }, [appliedFilters, fetch]);

  // 当前页导出数据只在列表变化时重新映射，不跟随勾选状态重算。
  const currentPageExportList = useMemo(() => list.map(data => ({
    ...data.items?.[0],
    remark: swapEnter(data?.items?.[0]?.remark ?? ''),
    order: { ...data, items: [] }
  })), [list]);

  // 导出字典上下文保持稳定，避免导出按钮在勾选时收到新对象。
  const exportContext = useMemo(() => ({
    currencys,
    saleTradeTerm,
    salePayMethod,
    saleShippingMethod,
    saleTypes,
    creditLevels,
    orgs
  }), [creditLevels, currencys, orgs, salePayMethod, saleShippingMethod, saleTradeTerm, saleTypes]);

  // 需要完整订单对象的功能在点击时按当前选中 id 临时获取，不把完整对象放进 state。
  const getSelectedSales = useCallback(() => {
    return selectedSaleIdsRef.current
      .map(id => {
        const key = String(id);
        return selectedSaleRowsRef.current.get(key) ?? listRef.current.find(sale => String(sale.id) === key);
      })
      .filter(Boolean) as Array<SaleOrderItemState>;
  }, []);

  // PI 导出需要 item 结构，延迟到点击导出模板时再组装。
  const getSelectedSaleItems = useCallback(() => {
    const items: Array<SaleItemState> = [];
    getSelectedSales().forEach(sale => {
      items.push(...sale.items.map(item => ({
        ...item,
        order: {
          ...sale,
          items: []
        }
      })))
    })
    return items
  }, [getSelectedSales]);

  const selectSaleOrders = useMemo(() => {
    if (!!!selectPSaleIds?.length) return [];
    return list.filter(l => selectPSaleIds.includes(l.id))
  }, [list, selectPSaleIds]);

  const handleOpenSwapPur = () => {
    if (!!!selectPSaleIds.length) return notification.error({ message: t('product.plSelectSaleOrder') });
    let rows = list.filter(row => selectPSaleIds.includes(row.id));
    if (!!!rows[0]?.customer?.wmsApi) {
      notification.error({ message: t('sale.notApi'), description: `${rows[0]?.customer?.shortName ?? ''}` })
      return
    }
    let customerId = rows[0]?.customerId;
    if (!!customerId) {
      if (rows.find(r => r.customerId !== customerId)) return notification.error({ message: t('sale.onlyOneCustomer') })
      else setSwapPurs([...rows]);
    } else notification.error({ message: t('product.plSelectSaleOrder') })
  };

  // 批量并发转正式订单，成功的移除，失败的提示并保留勾选。
  const handleToOfficial = useCallback((sales: SaleOrderItemState[]) => {
    if (!sales.length) return notification.error({ placement: 'top', message: t('product.plSelectSaleOrder') });
    // 已关联需求清单的订单不能转正式订单
    if (sales.some(sale => !!sale.requirement)) {
      return notification.error({ placement: 'top', message: t('sale.toOfficialHasRequirement') });
    }
    modal.confirm({
      title: t('common.tip'),
      content: t('sale.toOfficialConfirm', { count: sales.length }),
      onOk: async () => {
        const results = await Promise.allSettled(sales.map(sale => api.updateByKey(sale.id, { key: 'preApproval', value: 1 })));
        const failedSales = sales.filter((_, index) => results[index].status === 'rejected');
        rmItems(sales.filter((_, index) => results[index].status === 'fulfilled').map(sale => sale.id));
        setSelectSaleIds(failedSales.map(sale => sale.id));
        selectedSaleRowsRef.current = new Map(failedSales.map(sale => [sale.id, sale]));
        notification[failedSales.length ? 'error' : 'success']({
          placement: 'top',
          message: t(failedSales.length ? 'sale.toOfficialError' : 'sale.toOfficialSuccess'),
          description: failedSales.map(sale => sale.number).join(', '),
        });
      },
    });
  }, [modal, rmItems, t]);

  // 顶部工具栏包含多个导出组件，保持 memo 后只在必要状态变化时重建。
  const headerChildren = useMemo(() => (
    <Flex style={{ width: "100%" }} justify="space-between">
      <div>
        <Flex gap={10}>
          {pageId === PageId.MC_NOT_PASS && <Button
            onClick={() => setOpen(true)}
            type="primary"
          >
            {t("requirement.addRequirement")}
          </Button>}
          {pageId === PageId.SALE_NOT_PASS && <Button
            onClick={() => setAddOpen(true)}
            type="primary"
          >
            {t("sale.addRequirement")}
          </Button>}
          <ExportExcelButton<SaleItemState>
            list={currentPageExportList}
            columns={orderColumns}
            defaultSelectedColumns={['sale.orderNumber', 'sale.innerNumber', 'product.code']}
            dataMapper={orderDataMapper}
            fileName="op.xlsx"
            sheetName="Sheet1"
            context={exportContext}
          />
          <ExportExcelButton<SaleItemState>
            list={currentPageExportList}
            isAll
            loadData={handleExportAll}
            columns={orderColumns}
            defaultSelectedColumns={['sale.orderNumber', 'sale.innerNumber', 'product.code']}
            dataMapper={orderDataMapper}
            fileName="op.xlsx"
            sheetName="Sheet1"
            context={exportContext}
          />
          {pageId && [PageId.SALE_NOT_PASS, PageId.SALE_OFFICIAL, PageId.F_SALE_OFFICIAL].includes(pageId) && <ExportExcelToPI getList={getSelectedSaleItems} disabled={selectPSaleIds.length === 0} />}
          {(pageId === PageId.SALE_OFFICIAL || pageId === PageId.F_SALE_OFFICIAL) && hasPermission(curUser, [UserPermissionEnum.SAEL_OUTBOUND]) && <Button type="primary" disabled={selectPSaleIds.length === 0} onClick={() => setSaleOpen(true)}>{t('order.saleOut')}</Button>}
          {pageId === PageId.SALE_NOT_PASS && <Button type="dashed" onClick={() => handleOpenSwapPur()} >{t('sale.purSwap')}</Button>}
          <PrintSaleOrder selectedRows={selectSaleOrders} disabled={false} onEnd={() => { }} onStart={() => { }} />
          {pageId === PageId.SALE_NOT_PASS && hasPermission(curUser, [UserPermissionEnum.SALE_TO_OFFICIAL]) && <Button
            type="primary"
            disabled={selectPSaleIds.length === 0}
            onClick={() => handleToOfficial(getSelectedSales())}
          >
            {t("sale.toOfficial")}
          </Button>}
        </Flex>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <>
              <SelectCustomer
                value={customerId || undefined}
                onChange={value =>
                  updateDraftFilters({ customerId: value ?? "" })
                }
                allowClear
              />
              <RangePicker
                size="small"
                value={[startTime, endTime]}
                onChange={value =>
                  updateDraftFilters({
                    startTime: value?.[0] ?? null,
                    endTime: value?.[1] ?? null,
                  })
                }
              />
              <Tooltip title={t("sale.orderSearchTip")}>
                <Input
                  size="small"
                  placeholder={t("sale.orderSearchTip")}
                  style={{ width: 200 }}
                  value={keyword}
                  onChange={event =>
                    updateDraftFilters({ keyword: event.target.value })
                  }
                  onPressEnter={submit}
                />
              </Tooltip>
            </>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  ), [curUser, currentPageExportList, customerId, endTime, exportContext, getSelectedSaleItems, getSelectedSales, handleClearFilters, handleExportAll, handleSearch, handleToOfficial, keyword, listLoading, orderColumns, pageId, selectPSaleIds.length, startTime, t, updateDraftFilters]);

  // rowSelection 对象稳定后，勾选变化只更新 selectedRowKeys，不额外重建表格配置。
  const rowSelection: TableProps<AnyObject>['rowSelection'] | undefined = useMemo(() => pageId && [PageId.SALE_NOT_PASS, PageId.SALE_OFFICIAL, PageId.F_SALE_OFFICIAL].includes(pageId) ? {
    selectedRowKeys: selectPSaleIds,
    preserveSelectedRowKeys: true,
    onChange: (selectedRowKeys: React.Key[], selectedRows: AnyObject[]) => {
      // 跨页选中只把完整行对象写进 ref 缓存，不放进 state 触发表格重渲染。
      selectedRows.forEach(row => {
        if (row?.id) {
          selectedSaleRowsRef.current.set(String(row.id), row as SaleOrderItemState);
        }
      });
      const selectedIdSet = new Set(selectedRowKeys.map(String));
      Array.from(selectedSaleRowsRef.current.keys()).forEach(id => {
        if (!selectedIdSet.has(id)) {
          selectedSaleRowsRef.current.delete(id);
        }
      });
      setSelectSaleIds(selectedRowKeys)
    },
  } : undefined, [pageId, selectPSaleIds]);

  // 右侧详情面板只在详情数据或页面变化时更新，避免被勾选状态牵连。
  const RightInfo: ReactNode = useMemo(() => {
    if ((pageId === PageId.SALE_OFFICIAL || pageId === PageId.F_SALE_OFFICIAL) && !!detail) {
      if (detail) return (
        <div style={{ padding: 10, height: "100%", width: "100%", overflow: "auto", position: "relative" }}>
          <Button size="large"
            style={{ position: "absolute", right: 10, top: 10 }}
            icon={<CloseOutlined />} type="text"
            onClick={() => setDetail(null)}
          />
          <ItemsOrderForm onClose={() => setDetail(null)}
            onFormSuccess={order => {
              updateItem(order);
              setDetail(current => current?.id === order.id ? order : current)
            }} editMode="READ"
            leftTitle={<SText text={t('requirement.saleOrderNumber') + detail.number} />}
            recordData={detail}
            getContainer={false}
            animation={false}
            mask={false}
            transitionName=""
            maskTransitionName=""
            style={{ top: 0 }}
            noModal
            autoHeight
          />
        </div>
      )
      else return <Empty style={{ marginTop: '20vh' }} />
    } else return null
  }, [pageId, detail, t, updateItem])

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        rowKey="id"
        loading={listLoading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={handleTableChange}
        rowSelection={rowSelection}
        rightContent={RightInfo}
      />
      {pageId === PageId.MC_NOT_PASS && (
        <RequirementHandleAdd open={open} setOpen={setOpen} />
      )}
      {
        requirement && (
          <RequirementDetail
            open={!!requirement}
            onCancel={() => setRequirement(null)}
            handleCancel={() => setRequirement(null)}
            id={requirement?.requirement?.id ?? ''} number={""}
            handleOk={(type?: "close" | "del") => {
              setRequirement(null)
              if (type === 'del') {
                rmItem(requirement.id)
              }
            }}
          />
        )
      }
      {addOpen && <ItemsOrderForm onClose={() => setAddOpen(false)} onFormSuccess={order => {
        addItem(order);
        setAddOpen(false);
      }} editMode="ADD" leftTitle={t("common.add")} />}
      {
        detail && pageId === PageId.SALE_NOT_PASS && <ItemsOrderForm onClose={() => setDetail(null)} onFormSuccess={(order, _, keepOpen = false) => {
          if (order.preApproval === 1) {
            rmItem(order.id);
            selectedSaleRowsRef.current.delete(order.id);
            setSelectSaleIds(ids => ids.filter(id => String(id) !== order.id));
          } else updateItem(order);
          !keepOpen && setDetail(null);
        }} editMode="EDIT"
          leftTitle={<SText text={t('requirement.saleOrderNumber') + detail.number} />} recordData={detail} />
      }

      {financeItem && (
        <CreateRequirement
          saleOrder={financeItem}
          item={financeItem?.items?.[0]}
          open={!!financeItem}
          handleCancel={() => setFinanceItem(null)}
          handleAdd={() => {
            rmItem(financeItem.id)
            // passed(financeItem)
          }}
        />
      )}

      {
        outOrder && (
          <ChangeForm
            title={t('warehouse.outQuantity')}
            numberTip={t('warehouse.outQuantity')}
            numberPlTip={t('warehouse.plOutQuantity')}
            formTitle={t('warehouse.outQuantity')}
            order={{
              sourceId: outOrder.id,
              productId: outOrder.productId,
              changeType: ChangeEnum.OUT,
              sourceType: SourceTypeEnum.SALE_ITEM,
            }}
            setOrder={() => setOutOrder(null)}
          />
        )
      }
      {
        swapSale && <SaleSwapModal originOrder={swapSale} open={!!swapSale} onClose={() => setSwapSale(null)} onSwapSuccess={() => { }} />
      }
      {
        swapPurs.length > 0 && <PurSwapModal saleOrders={swapPurs} open onClose={() => setSwapPurs([])} onSwapSuccess={() => { }} />
      }

      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
      {saleOpen && <SaleOut open={saleOpen} list={getSelectedSales()} onCancel={() => setSaleOpen(false)} />}
    </Flex>
  );
};

export default OrderList;
